import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";
import { useOndi } from "../../context/OndiContext";
import { useTheme } from "../../context/ThemeContext";
import { OndiCharacter } from "./OndiCharacter";

const BASE_STEPS = [
  {
    id: "mode-toggle",
    selector: '[data-testid="mode-toggle"]',
    title: "Modo General ↔ Modo Negocio",
    body:
      "Este switch cambia toda la app entre Modo General (cyan, uso personal) y Modo Negocio (magenta, tu marca). Se guarda automáticamente.",
    route: "/",
  },
  {
    id: "nav-chats",
    selector: '[data-testid="nav-chats"]',
    title: "Chats",
    body:
      "Tus conversaciones 1:1 y grupales. Recibos de lectura, presencia en tiempo real y typing incluidos.",
    route: "/",
  },
  {
    id: "nav-pulsos",
    selector: '[data-testid="nav-pulsos"]',
    title: "OndaPulse",
    body:
      "El feed de pulsos: encuestas (Vibe Check), estados (Mood Wave) y textos cortos. Todo expira a las 24 h.",
    route: "/pulsos",
  },
  {
    id: "publish-pulse",
    selector: '[data-testid="publish-pulse-btn"]',
    title: "Publicar OndaPulse",
    body:
      "Desde aquí publicas tu propio pulso. En Modo Negocio, este botón crea Flash Offers con descuento.",
    route: "/pulsos",
  },
  {
    id: "nav-perfil",
    selector: '[data-testid="nav-perfil"]',
    title: "Perfil y @OndaTag",
    body:
      "Edita tu nombre, bio, avatar y ve tu @OndaTag. Los negocios verificados encontrarán aquí su auto-respuesta.",
    route: "/perfil",
  },
];

const NEGOCIO_EXTRA = {
  id: "ad-walker",
  selector: '[data-testid="ad-walker-open"]',
  title: "Ad-Walker",
  body:
    "En Modo Negocio, envía una promoción a la calle por Ad-Walker. Piloto por ahora, sin GPS ni mapas.",
  route: "/pulsos",
};

function useAnchorRect(selector, tick) {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    const compute = () => {
      const el = document.querySelector(selector);
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
        right: r.right,
        bottom: r.bottom,
      });
    };
    compute();
    const raf = requestAnimationFrame(compute);
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [selector, tick]);
  return rect;
}

export function OndiTour() {
  const { finishTour } = useOndi();
  const { isNegocio } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [idx, setIdx] = useState(0);
  const [tick, setTick] = useState(0);

  const steps = useMemo(() => {
    const s = [...BASE_STEPS];
    if (isNegocio) s.splice(4, 0, NEGOCIO_EXTRA);
    return s;
  }, [isNegocio]);

  const step = steps[idx];

  // Navigate to the correct route for each step before computing anchor
  useEffect(() => {
    if (!step) return;
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
    }
    // Kick a recompute after navigation completes
    const t = setTimeout(() => setTick((v) => v + 1), 300);
    return () => clearTimeout(t);
  }, [step, location.pathname, navigate]);

  const rect = useAnchorRect(step?.selector || "", tick);

  useEffect(() => {
    // Poll a couple of times in case DOM isn't ready yet (e.g. bottom nav on route change)
    const timers = [200, 500, 900].map((ms) =>
      setTimeout(() => setTick((v) => v + 1), ms),
    );
    return () => timers.forEach(clearTimeout);
  }, [idx]);

  if (!step) return null;

  const onNext = () => {
    if (idx + 1 >= steps.length) {
      finishTour();
    } else {
      setIdx(idx + 1);
    }
  };

  const onSkip = () => finishTour();

  const totalSteps = steps.length;
  const isLast = idx + 1 === totalSteps;

  return createPortal(
    <div
      className="fixed inset-0 z-[80]"
      data-testid="ondi-tour"
      role="dialog"
      aria-label="Tour de ONDI"
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/70 pointer-events-auto" onClick={onSkip} />
      {/* Spotlight */}
      {rect && (
        <div
          className="absolute rounded-2xl pointer-events-none transition-all duration-300 ease-out"
          style={{
            top: Math.max(4, rect.top - 8),
            left: Math.max(4, rect.left - 8),
            width: rect.width + 16,
            height: rect.height + 16,
            boxShadow:
              "0 0 0 4px var(--accent), 0 0 40px 12px var(--accent-glow), 0 0 0 9999px rgba(0,0,0,0.72)",
          }}
          data-testid="ondi-tour-spotlight"
        />
      )}

      {/* Tooltip card */}
      <TourCard
        rect={rect}
        step={step}
        idx={idx}
        total={totalSteps}
        onNext={onNext}
        onSkip={onSkip}
        isLast={isLast}
      />
    </div>,
    document.body,
  );
}

function TourCard({ rect, step, idx, total, onNext, onSkip, isLast }) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 400;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const cardW = Math.min(340, vw - 24);

  // Position the card near the spotlight; on mobile, dock to bottom.
  let style = {};
  const isMobile = vw < 640;
  if (isMobile || !rect) {
    style = {
      left: 12,
      right: 12,
      bottom: 96,
      width: "auto",
      maxWidth: cardW,
      margin: "0 auto",
    };
  } else {
    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;
    const preferBelow = spaceBelow > 220 || spaceBelow > spaceAbove;
    const top = preferBelow ? Math.min(vh - 220, rect.bottom + 16) : Math.max(16, rect.top - 220);
    let left = rect.left + rect.width / 2 - cardW / 2;
    left = Math.max(12, Math.min(vw - cardW - 12, left));
    style = { top, left, width: cardW };
  }

  return (
    <div
      className="absolute q-glass rounded-3xl border border-white/10 p-5 pointer-events-auto shadow-2xl"
      style={style}
      data-testid="ondi-tour-card"
    >
      <div className="flex items-start gap-3 mb-3">
        <OndiCharacter size={44} waving />
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest q-accent-text">
            ONDI · paso {idx + 1} de {total}
          </div>
          <div className="font-display text-lg font-bold text-slate-100 mt-0.5">
            {step.title}
          </div>
        </div>
        <button
          type="button"
          onClick={onSkip}
          className="text-slate-400 hover:text-slate-100 p-1"
          aria-label="Cerrar tour"
        >
          <X size={16} />
        </button>
      </div>
      <p className="text-sm text-slate-200 leading-relaxed">{step.body}</p>
      <div className="flex items-center justify-between mt-4 gap-2">
        <button
          type="button"
          onClick={onSkip}
          className="q-btn-ghost !py-1.5 !px-3 text-xs"
          data-testid="ondi-tour-skip"
        >
          Saltar tour
        </button>
        <button
          type="button"
          onClick={onNext}
          className="q-btn-primary inline-flex items-center gap-1 !py-1.5 !px-3 text-xs"
          data-testid="ondi-tour-next"
        >
          {isLast ? (
            <>
              <Check size={14} /> Terminar
            </>
          ) : (
            <>
              Siguiente <ArrowRight size={14} />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
